/* eslint-disable react/display-name */
/* eslint-disable prefer-template */

import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import { FormBuilderData } from 'types/formBuilder';
import { deltaToHtml } from 'utils/string.utils';

const { Title, Text } = Typography;

type Field = {
  name: string;
  label: string;
  type: string;
  options?: { label: string; value: any }[];
};

type Props = {
  data: {
    client: string;
    requestName: string;
    owner: string;
    estimate: string;
    answers: Record<string, any>;
  };
};

const fixedFields: Record<string, Field> = {
  subject: {
    name: 'subject',
    label: 'Request name',
    type: 'input',
  },
  target_date: {
    name: 'target_date',
    label: 'Deadline',
    type: 'date',
  },
  skip_estimate_approval: {
    name: 'skip_estimate_approval',
    label: 'Skip estimate approval?',
    type: 'radio',
    options: [
      {
        label: "Yes, I'd like to skip the estimate approval and start the project right away.",
        value: true,
      },
      {
        label: 'No, I would like to discuss this with someone further before getting started.',
        value: false,
      },
    ],
  },
  uploads: {
    name: 'uploads',
    label: 'Upload any assets related to this project (optional)',
    type: 'upload',
  },
};

function isDeltaFormat(value: string): boolean {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && Array.isArray(parsed.ops);
  } catch {
    return false;
  }
}

function getFieldDefinition(fieldName: string, listFormsData: FormBuilderData[]): Field | undefined {
  const allFields: Field[] = listFormsData.flatMap((form: any) => {
    if (form.form) {
      return form.form.flatMap((field: any) => {
        if (field.type === 'formGroup' && Array.isArray(field.fields)) {
          return field.fields;
        }
        return field;
      });
    }
    return [];
  });

  return allFields.find((field) => field.name === fieldName) || fixedFields[fieldName];
}

function getLabelFromValue(field: Field, value: any): string {
  if (!field.options) return String(value);

  if (Array.isArray(value)) {
    return value.map((v) => field.options?.find((opt) => opt.value === v)?.label ?? v).join(', ');
  }

  return field.options.find((opt) => opt.value === value)?.label ?? value;
}

function getCheckboxLabels(field: Field, value: any): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.map((v) => field.options?.find((opt) => opt.value === v)?.label ?? String(v));
}

function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function getStringValue(field: Field, value: any): string {
  switch (field.type) {
    case 'checkbox':
    case 'select':
    case 'radio':
      return getLabelFromValue(field, value);
    case 'textArea':
    case 'textEditor': {
      const str = String(value);
      const html = isDeltaFormat(str) ? deltaToHtml(str) : str;
      return htmlToPlainText(html);
    }
    case 'date':
      return dayjs(value).format('MM/DD/YYYY');
    default:
      return String(value ?? '-');
  }
}

export type PrintData = {
  client: string;
  requestName: string;
  owner: string;
  estimate: string;
  answers: Record<string, any>;
};

export function buildMarkdown(data: PrintData, listFormsData: FormBuilderData[]): string {
  const lines: string[] = [`**Client:** ${data.client}`, `# ${data.requestName}`, ''];

  Object.entries(data.answers).forEach(([key, value]) => {
    if (!value) return;
    const field = getFieldDefinition(key, listFormsData);
    const label = field?.label ?? key;
    const text = field ? getStringValue(field, value) : String(value);
    lines.push(`**${label}:**`, text, '');
  });

  return lines.join('\n');
}

export function buildRichHtml(data: PrintData, listFormsData: FormBuilderData[]): string {
  const parts: string[] = [
    `<p><span style="color:#0044CC;font-weight:500;margin:0 0 4px">Client:</span> ${data.client}</p>`,
    `<p><span style="color:#0044CC;font-weight:500;margin:0 0 4px">Owner:</span> ${data.owner}</p>`,
    `<p><span style="color:#0044CC;font-weight:500;margin:0 0 4px">Request name:</span> ${data.requestName}</p>`,
    `<p><span style="color:#0044CC;font-weight:500;margin:0 0 4px">Estimate:</span> ${data.estimate}</p><br/>`,
    `<h2 style="font-size:22px;font-weight:700;margin:0 0 16px">${data.requestName}</h2>`,
  ];

  Object.entries(data.answers).forEach(([key, value]) => {
    if (!value) return;
    const field = getFieldDefinition(key, listFormsData);
    const label = field?.label ?? key;

    const isRich = field?.type === 'textEditor' || field?.type === 'textArea';
    let content: string;
    if (field?.type === 'checkbox') {
      const labels = getCheckboxLabels(field, value);
      const items = labels.map((l) => `<li>${l}</li>`).join('');
      content = `<ul style="margin:0;padding-left:20px">${items}</ul>`;
    } else if (isRich) {
      const str = String(value);
      content = isDeltaFormat(str) ? deltaToHtml(str) : str;
    } else {
      content = `<p style="margin:0">${field ? getStringValue(field, value) : String(value)}</p>`;
    }

    parts.push(
      '<div style="margin-bottom:16px">' +
        `<p style="color:#0044CC;font-weight:600;margin:0 0 6px">${label}:</p>` +
        content +
      '</div><br/>',
    );
  });

  return `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.6;padding:20px">${parts.join('')}</div>`;
}

function renderDisplayValue(field: Field, value: any): React.ReactNode {
  switch (field.type) {
    case 'checkbox': {
      const labels = getCheckboxLabels(field, value);
      return (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {labels.map((label) => <li key={label}>{label}</li>)}
        </ul>
      );
    }
    case 'select':
      return <Text>{getLabelFromValue(field, value)}</Text>;

    case 'textArea':
    case 'textEditor': {
      const str = String(value);
      const html = isDeltaFormat(str) ? deltaToHtml(str) : str;
      return <div dangerouslySetInnerHTML={{ __html: html }} />;
    }

    case 'radio':
      return <Text>{getLabelFromValue(field, value)}</Text>;

    case 'date':
      return <Text>{dayjs(value).format('MM/DD/YYYY')}</Text>;

    default:
      return <Text>{String(value ?? '-')}</Text>;
  }
}

export const PrintRequestForm = forwardRef<HTMLDivElement, Props>(({ data }, ref) => {
  const listFormsData = useSelector((state: RootState) => state.resources.requestForms);

  if (!data) return null;

  return (
    <div ref={ref} className="form-view w-full" style={{ padding: '20px' }}>
      <div className="mb-l flex flex-col">
        <div className="flex items-center" style={{ gap: 4 }}>
          <Text className="primary">Client:</Text>
          <Text>{data.client}</Text>
        </div>
        <div className="flex items-center" style={{ gap: 4 }}>
          <Text className="primary">Owner:</Text>
          <Text>{data.owner}</Text>
        </div>
        <div className="flex items-center" style={{ gap: 4 }}>
          <Text className="primary">Request name:</Text>
          <Text>{data.requestName}</Text>
        </div>
        <div className="flex items-center" style={{ gap: 4 }}>
          <Text className="primary">Estimate:</Text>
          <Text>{data.estimate}</Text>
        </div>
      </div>

      <Title className="mb-s" level={3}>
        {data.requestName}
      </Title>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {Object.entries(data.answers).map(([key, value]) => {
          if (!value) return undefined;

          const field = getFieldDefinition(key, listFormsData);
          const label = field?.label ?? key;
          const displayValue = field ? renderDisplayValue(field, value) : <Text>{String(value)}</Text>;

          return (
            <div key={key} style={{ marginBottom: 16 }}>
              <Text className="primary">{label}:</Text>
              <div style={{ padding: '10px 0px 20px 0px' }}>{displayValue}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
